import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { translatePosition } from "../util/positionNames.tsx";
import {
	getMarketComparison,
	hasMarketReference,
	type PositionSalaryMap,
} from "../../../../common/positionSalary.ts";

// G5-1: the in-line market-reference row for a negotiation card. Kept in its own
// module (not inside NegotiationList) so the UI branch is unit-testable without
// importing the heavy view (toWorker/logEvent/etc.). Renders null when there are
// too few comparable contracts to quote a meaningful median (hasMarketReference).

const comparisonText = (kind: ReturnType<typeof getMarketComparison>) => {
	if (kind === "above") {
		return i18n.t("negotiationList.marketRef.above", {
			ns: "ui",
			defaultValue: "asking above market",
		});
	}
	if (kind === "below") {
		return i18n.t("negotiationList.marketRef.below", {
			ns: "ui",
			defaultValue: "asking below market",
		});
	}
	return i18n.t("negotiationList.marketRef.near", {
		ns: "ui",
		defaultValue: "asking in line with market",
	});
};

export const NegotiationMarketRef = ({
	asking,
	pos,
	positionSalary,
}: {
	asking: number;
	pos: string;
	positionSalary: PositionSalaryMap;
}) => {
	const stat = positionSalary[pos];
	if (!hasMarketReference(stat)) {
		return null;
	}

	const comparison = getMarketComparison(asking, stat);

	return (
		<div
			className="small text-body-secondary"
			data-negotiation-market-ref=""
			data-market-comparison={comparison}
		>
			{i18n.t("negotiationList.marketRef.line", {
				ns: "ui",
				defaultValue:
					"League {{pos}} pay: {{median}} median · {{p25}}–{{p75}} typical",
				pos: translatePosition(pos, "short"),
				median: helpers.formatCurrency(stat.median, "M"),
				p25: helpers.formatCurrency(stat.p25, "M"),
				p75: helpers.formatCurrency(stat.p75, "M"),
			})}
			{" · "}
			{comparisonText(comparison)}
		</div>
	);
};

export default NegotiationMarketRef;
